/**
 * HAZIR TARİH DÖNEMLERİ (kullanıcı, 12.10.2026): sitede iki tarih arası
 * seçilen her yerde "bu ay", "geçen çeyrek", "son 3 yıl" gibi hazır seçenekler.
 *
 * Kurallar (hepsi testte kilitli, `tarihDonemleri.test.ts`):
 *   - Uçlar DAHİL; "bugün" kullanıcının YEREL takvim günü (çağıran verir).
 *   - "Bu …" dönemin ilk gününden bugüne; "geçen / önceki …" bir önceki
 *     dönemin tamamı.
 *   - "Son N …" takvimde N birim önceki aynı günden bugüne (son 1 ay,
 *     12.10'da 12.09–12.10). Ay sonu taşmaz: 31.03'ten 1 ay önce 28/29.02.
 *   - Hafta pazartesi başlar.
 */

import type { IsoDate } from "@parafon/contracts";
import { M } from "../dil";

export type DonemKodu =
  | "bugun"
  | "dun"
  | "bu-hafta"
  | "son-1-hafta"
  | "gecen-hafta"
  | "bu-ay"
  | "son-1-ay"
  | "gecen-ay"
  | "son-2-ay"
  | "bu-ceyrek"
  | "son-3-ay"
  | "onceki-ceyrek"
  | "bu-yariyil"
  | "son-6-ay"
  | "gecen-yariyil"
  | "bu-yil"
  | "son-1-yil"
  | "gecen-yil"
  | "son-2-yil"
  | "son-3-yil"
  | "son-4-yil"
  | "son-5-yil"
  | "son-10-yil";

export interface TarihAraligi {
  readonly from: IsoDate;
  readonly to: IsoDate;
}

// ---------------------------------------------------------------------------
// Takvim yardımcıları (UTC üzerinden: yalnızca takvim aritmetiği, saat yok)
// ---------------------------------------------------------------------------

function parca(d: IsoDate): [number, number, number] {
  return [Number(d.slice(0, 4)), Number(d.slice(5, 7)), Number(d.slice(8, 10))];
}

function iso(y: number, m: number, g: number): IsoDate {
  return new Date(Date.UTC(y, m - 1, g)).toISOString().slice(0, 10);
}

function ayinGunSayisi(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function gunKaydir(d: IsoDate, gun: number): IsoDate {
  const [y, m, g] = parca(d);
  return iso(y, m, g + gun);
}

/** N ay geri (ay sonu taşmaz: 31.03 − 1 ay = 28/29.02). */
export function ayGeri(d: IsoDate, ay: number): IsoDate {
  const [y, m, g] = parca(d);
  const toplam = y * 12 + (m - 1) - ay;
  const yy = Math.floor(toplam / 12);
  const mm = (toplam % 12) + 1;
  return iso(yy, mm, Math.min(g, ayinGunSayisi(yy, mm)));
}

/** `ay` uzunluğunda dönemlerin (1 ay, 3 çeyrek, 6 yarıyıl, 12 yıl) başı. */
function donemBasi(d: IsoDate, ay: number): IsoDate {
  const [y, m] = parca(d);
  return iso(y, Math.floor((m - 1) / ay) * ay + 1, 1);
}

/** Bir önceki `ay` uzunluklu dönemin tamamı. */
function oncekiDonem(d: IsoDate, ay: number): TarihAraligi {
  const bas = donemBasi(d, ay);
  return { from: ayGeri(bas, ay), to: gunKaydir(bas, -1) };
}

function haftaBasi(d: IsoDate): IsoDate {
  const [y, m, g] = parca(d);
  const haftaninGunu = (new Date(Date.UTC(y, m - 1, g)).getUTCDay() + 6) % 7; // pazartesi = 0
  return gunKaydir(d, -haftaninGunu);
}

// ---------------------------------------------------------------------------
// Dönemler (kullanıcının verdiği sırayla)
// ---------------------------------------------------------------------------

export interface Donem {
  readonly kod: DonemKodu;
  readonly ad: string;
  readonly aralik: (bugun: IsoDate) => TarihAraligi;
}

const sonAy = (ay: number) => (b: IsoDate): TarihAraligi => ({ from: ayGeri(b, ay), to: b });
const buDonem = (ay: number) => (b: IsoDate): TarihAraligi => ({ from: donemBasi(b, ay), to: b });

export const DONEMLER: readonly Donem[] = [
  { kod: "bugun", ad: M.grafik.donem.bugun, aralik: (b) => ({ from: b, to: b }) },
  { kod: "dun", ad: M.grafik.donem.dun, aralik: (b) => ({ from: gunKaydir(b, -1), to: gunKaydir(b, -1) }) },
  { kod: "bu-hafta", ad: M.grafik.donem.buHafta, aralik: (b) => ({ from: haftaBasi(b), to: b }) },
  { kod: "son-1-hafta", ad: M.grafik.donem.son1Hafta, aralik: (b) => ({ from: gunKaydir(b, -7), to: b }) },
  {
    kod: "gecen-hafta",
    ad: M.grafik.donem.gecenHafta,
    aralik: (b) => ({ from: gunKaydir(haftaBasi(b), -7), to: gunKaydir(haftaBasi(b), -1) }),
  },
  { kod: "bu-ay", ad: M.grafik.donem.buAy, aralik: buDonem(1) },
  { kod: "son-1-ay", ad: M.grafik.donem.son1Ay, aralik: sonAy(1) },
  { kod: "gecen-ay", ad: M.grafik.donem.gecenAy, aralik: (b) => oncekiDonem(b, 1) },
  { kod: "son-2-ay", ad: M.grafik.donem.son2Ay, aralik: sonAy(2) },
  { kod: "bu-ceyrek", ad: M.grafik.donem.buCeyrek, aralik: buDonem(3) },
  { kod: "son-3-ay", ad: M.grafik.donem.son3Ay, aralik: sonAy(3) },
  { kod: "onceki-ceyrek", ad: M.grafik.donem.oncekiCeyrek, aralik: (b) => oncekiDonem(b, 3) },
  { kod: "bu-yariyil", ad: M.grafik.donem.buYariyil, aralik: buDonem(6) },
  { kod: "son-6-ay", ad: M.grafik.donem.son6Ay, aralik: sonAy(6) },
  { kod: "gecen-yariyil", ad: M.grafik.donem.gecenYariyil, aralik: (b) => oncekiDonem(b, 6) },
  { kod: "bu-yil", ad: M.grafik.donem.buYil, aralik: buDonem(12) },
  { kod: "son-1-yil", ad: M.grafik.donem.son1Yil, aralik: sonAy(12) },
  { kod: "gecen-yil", ad: M.grafik.donem.gecenYil, aralik: (b) => oncekiDonem(b, 12) },
  { kod: "son-2-yil", ad: M.grafik.donem.son2Yil, aralik: sonAy(24) },
  { kod: "son-3-yil", ad: M.grafik.donem.son3Yil, aralik: sonAy(36) },
  { kod: "son-4-yil", ad: M.grafik.donem.son4Yil, aralik: sonAy(48) },
  { kod: "son-5-yil", ad: M.grafik.donem.son5Yil, aralik: sonAy(60) },
  { kod: "son-10-yil", ad: M.grafik.donem.son10Yil, aralik: sonAy(120) },
];

export function donemAraligi(kod: DonemKodu, bugun: IsoDate): TarihAraligi {
  const d = DONEMLER.find((x) => x.kod === kod);
  if (d === undefined) throw new Error(`Bilinmeyen dönem: ${kod}`);
  return d.aralik(bugun);
}

/** Aralık hazır bir döneme denk geliyorsa onun kodu (ilk eşleşen), yoksa null. */
export function eslesenDonem(aralik: TarihAraligi, bugun: IsoDate): DonemKodu | null {
  for (const d of DONEMLER) {
    const a = d.aralik(bugun);
    if (a.from === aralik.from && a.to === aralik.to) return d.kod;
  }
  return null;
}

/** Kullanıcının YEREL takvim günü. `toISOString()` UTC verir; İstanbul'da 00:00–03:00 arası dünü. */
export function bugunYerelIso(now: Date = new Date()): IsoDate {
  const y = String(now.getFullYear());
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
